import type { File, FileRetrieve, PaginatedFileList, FileUpdateRequest, FileRevision } from '@openapi'
import type { GetRecentFilesPayload, ListFilesPayload } from '@sdk/payload.types'
import type { AllFileCreateRequest, DeviceFile } from '@lib/types/files'

export function getFiles(
  payload: ListFilesPayload & { filters?: Record<string, any>; fields?: string },
): Promise<PaginatedFileList> {
  const p = { ...payload }

  if (p.type) {
    p.type = Array.isArray(p.type) ? p.type : [p.type]
  }

  if (p.content_type) {
    p.content_type = (
      Array.isArray(p.content_type) ? p.content_type : [p.content_type]
    ) as ListFilesPayload['content_type']
  }

  return this.API.request('get_files', p)
}

export function getRecentFiles(payload?: GetRecentFilesPayload): Promise<File[]> {
  return this.API.request('get_recent_files', payload || {})
}

export function downloadFile(payload: { file_id: File['id'] }): Promise<null> {
  return this.API.request('download_file', payload)
}

export function getAppConfig(payload: { app_name: string }): Promise<Record<string, any>> {
  return this.API.request('get_app_config', payload)
}

export function getFile(payload: { id?: FileRetrieve['id']; file_id?: FileRetrieve['id'] }): Promise<FileRetrieve> {
  const { id, file_id } = payload
  return this.API.request('get_file', { file_id: id || file_id })
}

export function getFileRevisionData(payload: {
  file_id: FileRetrieve['id']
  revision_id: FileRevision['id']
}): Promise<FileRetrieve> {
  return this.API.request('get_file_revision_data', payload)
}

export function updateFile(payload: FileUpdateRequest & { id: File['id'] }): Promise<FileRetrieve> {
  return this.API.request('update_file', payload)
}

export function deleteFile(payload: { file_id: File['id'] }): Promise<File['id']> {
  return this.API.request('delete_file', payload)
}

export function createFile(payload: Omit<AllFileCreateRequest, 'instance_id'>): Promise<FileRetrieve> {
  return this.API.request('create_file', payload)
}

/**
 * Dispatches iOS native file selector and returns the selected file.
 *
 * @example
 * // iOS only method to select a file from the device.
 * api.selectDeviceFile()
 */
export function selectDeviceFile(): Promise<DeviceFile> {
  return this.API.request('select_device_file')
}
